import { createAsyncThunk } from '@reduxjs/toolkit'
import { instance } from '../api/api'
import { GetProductsResponseType, ProductsType } from './types'

export const getProducts = createAsyncThunk(
  'products/getProducts',
  async (
    {
      token,
      page,
      name,
      search,
      itemsPerPage,
      city,
      size,
      address,
      description,
    }: {
      token: string
      page: number
      name?: string
      search?: string
      itemsPerPage: number
      city?: string
      size?: string
      address?: string
      description?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetProductsResponseType>('products', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
          ...(city ? { city: city } : {}),
          ...(size ? { size: size } : {}),
          ...(name ? { name: name } : {}),
          ...(address ? { address: address } : {}),
          ...(description ? { description: description } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Handle 401 error (e.g., redirect to login, notify user)
        return thunkAPI.rejectWithValue('Unauthorized')
      }

      // Handle other errors
      return thunkAPI.rejectWithValue(error?.response?.data?.message || 'An error occurred.')
    }
  },
)

export const getProductFromCompany = createAsyncThunk(
  'products/getProductFromCompany',
  async (
    {
      token,
      page,
      itemsPerPage,
      search,
    }: {
      token: string
      page: number
      itemsPerPage: number
      search?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetProductsResponseType>('products', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getCurrentProduct = createAsyncThunk(
  'products/getCurrentProduct',
  async ({ productId, token }: { productId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.get<ProductsType>(`products/${productId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async ({ formData, token }: { formData: any; token: string }, thunkAPI) => {
    try {
      const response = await instance.post<any>('products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const deleteCurrentFileFromProduct = createAsyncThunk(
  'products/deleteCurrentFileFromProduct',
  async (
    {
      productId,
      filesToDelete,
      token,
    }: { productId: string | undefined; filesToDelete: string[]; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.delete<ProductsType>(`products/${productId}/files`, {
        data: { filesToDelete },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const pushNewFilesToProduct = createAsyncThunk(
  'products/pushNewImagesToProduct',
  async (
    { productId, formData, token }: { productId: string | undefined; formData: any; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.put<ProductsType>(`products/${productId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data; charset=utf-8',
          authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const changeProductMainInformation = createAsyncThunk(
  'products/changeProductMainInformation',
  async (
    {
      token,
      productId,
      weight,
      price,
      quantity,
      name,
      description,
      measurement,
    }: // totalPrice
    {
      productId: string | number
      name?: string
      price?: string
      description?: string
      measurement?: string
      token: string
      quantity?: number | string
      weight?: string
      // totalPrice?: number | string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<ProductsType>(
        `products/${productId}`,
        {
          price,
          name,
          description,
          quantity,
          weight,
          measurement,
          // totalPrice
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const changeProductDescription = createAsyncThunk(
  'products/changeProductDescription',
  async (
    {
      productId,
      description,
      token,
    }: { productId: string | number | undefined; description: string; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<ProductsType>(
        `products/${productId}`,
        {
          description,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const deleteCurrentProduct = createAsyncThunk(
  'products/deleteCurrentProduct',
  async ({ productId, token }: { productId: string | undefined; token: string }, thunkAPI) => {
    try {
      const response = await instance.delete<ProductsType>(`products/${productId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
